// ============================================================================
// INVENTORY CORE SERVICES (ADR-0058 slice 1)
// Item categories, classifications, stock locations, inventory settings, items.
// One file because the five share the paged-result mapping and are always
// shipped together; each is still its own service instance.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import {
  itemCategoryEndpoints, classificationEndpoints, stockLocationEndpoints, inventorySettingsEndpoints, itemEndpoints,
  stockReportEndpoints, openingStockEndpoints,
} from '../endpoints/inventory.endpoints';
import type { ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID } from '../types';
import type {
  ItemCategoryDto, CreateItemCategoryRequest, UpdateItemCategoryRequest, ItemCategoryQueryParams,
  ClassificationDimensionDto, ClassificationValueDto, CreateClassificationDimensionRequest,
  UpdateClassificationDimensionRequest, ClassificationDimensionQueryParams, ClassificationValueQueryParams,
  StockLocationDto, CreateStockLocationRequest, UpdateStockLocationRequest, StockLocationQueryParams,
  InventorySettingsOverviewDto, SaveInventorySettingRequest, SetInventoryLedgerDefaultRequest,
  InventoryLedgerDefaultDto, LedgerOverrideDto, SaveLedgerOverrideRequest, LedgerOverrideQueryParams,
  ItemDto, CreateItemRequest, UpdateItemRequest, ItemQueryParams,
  StockPositionRowDto, StockPositionQueryParams, StockCardDto, StockCardQueryParams,
  OpeningStockDto, SaveOpeningStockRequest, OpeningStockQueryParams,
} from '../types/inventory.types';

type Query = Record<string, string | number | boolean | undefined | null>;

const withQuery = (endpoint: string, params?: object) =>
  params ? buildEndpointWithQuery(endpoint, params as Query) : endpoint;

async function getPaged<T>(endpoint: string, params?: object): Promise<PaginatedResponse<T>> {
  const response = await get<ApiResponse<BackendPagedResult<T>>>(withQuery(endpoint, params));
  const paged = response.data.data;
  return {
    success: response.data.success, message: response.data.message,
    data: Array.from(paged.items || []),
    pagination: {
      currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
      totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
    },
  };
}

async function getData<T>(endpoint: string): Promise<T> {
  const response = await get<ApiResponse<T>>(endpoint);
  return response.data.data;
}

async function isAvailable(endpoint: string, excludeId?: ID): Promise<boolean> {
  return getData<boolean>(excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint);
}

async function deleteVersioned(endpoint: string, rowVersion: number): Promise<EmptyResponse> {
  const response = await del<EmptyResponse>(buildEndpointWithQuery(endpoint, { rowVersion }));
  return response.data;
}

// ---------------------------------------------------------------------------

class ItemCategoryService {
  getAll(params?: ItemCategoryQueryParams) { return getPaged<ItemCategoryDto>(itemCategoryEndpoints.list(), params); }
  getById(id: ID) { return getData<ItemCategoryDto>(itemCategoryEndpoints.getById(id)); }
  async create(data: CreateItemCategoryRequest): Promise<ItemCategoryDto> {
    return (await post<ApiResponse<ItemCategoryDto>>(itemCategoryEndpoints.create(), data)).data.data;
  }
  async update(id: ID, data: UpdateItemCategoryRequest): Promise<ItemCategoryDto> {
    return (await put<ApiResponse<ItemCategoryDto>>(itemCategoryEndpoints.update(id), data)).data.data;
  }
  delete(id: ID, rowVersion: number) { return deleteVersioned(itemCategoryEndpoints.delete(id), rowVersion); }
  isCodeAvailable(code: string, excludeId?: ID) { return isAvailable(itemCategoryEndpoints.validateCode(code), excludeId); }
}

class ClassificationService {
  getAll(params?: ClassificationDimensionQueryParams) { return getPaged<ClassificationDimensionDto>(classificationEndpoints.list(), params); }
  getById(id: ID) { return getData<ClassificationDimensionDto>(classificationEndpoints.getById(id)); }
  async getActive(): Promise<ClassificationDimensionDto[]> {
    return Array.from((await getData<ClassificationDimensionDto[]>(classificationEndpoints.active())) || []);
  }
  getValues(params: ClassificationValueQueryParams) { return getPaged<ClassificationValueDto>(classificationEndpoints.values(), params); }
  async create(data: CreateClassificationDimensionRequest): Promise<ClassificationDimensionDto> {
    return (await post<ApiResponse<ClassificationDimensionDto>>(classificationEndpoints.create(), data)).data.data;
  }
  async update(id: ID, data: UpdateClassificationDimensionRequest): Promise<ClassificationDimensionDto> {
    return (await put<ApiResponse<ClassificationDimensionDto>>(classificationEndpoints.update(id), data)).data.data;
  }
  delete(id: ID, rowVersion: number) { return deleteVersioned(classificationEndpoints.delete(id), rowVersion); }
  isCodeAvailable(code: string, excludeId?: ID) { return isAvailable(classificationEndpoints.validateCode(code), excludeId); }
}

class StockLocationService {
  getAll(params?: StockLocationQueryParams) { return getPaged<StockLocationDto>(stockLocationEndpoints.list(), params); }
  /** Storage locations for item-screen pickers (needs item-master VIEW only). */
  lookup(params?: StockLocationQueryParams) { return getPaged<StockLocationDto>(stockLocationEndpoints.lookup(), params); }
  getById(id: ID) { return getData<StockLocationDto>(stockLocationEndpoints.getById(id)); }
  async create(data: CreateStockLocationRequest): Promise<StockLocationDto> {
    return (await post<ApiResponse<StockLocationDto>>(stockLocationEndpoints.create(), data)).data.data;
  }
  async update(id: ID, data: UpdateStockLocationRequest): Promise<StockLocationDto> {
    return (await put<ApiResponse<StockLocationDto>>(stockLocationEndpoints.update(id), data)).data.data;
  }
  delete(id: ID, rowVersion: number) { return deleteVersioned(stockLocationEndpoints.delete(id), rowVersion); }
  isCodeAvailable(code: string, excludeId?: ID) { return isAvailable(stockLocationEndpoints.validateCode(code), excludeId); }
}

class InventorySettingsService {
  get() { return getData<InventorySettingsOverviewDto>(inventorySettingsEndpoints.get()); }
  async savePolicy(data: SaveInventorySettingRequest): Promise<InventorySettingsOverviewDto> {
    return (await put<ApiResponse<InventorySettingsOverviewDto>>(inventorySettingsEndpoints.savePolicy(), data)).data.data;
  }
  async setLedgerDefault(data: SetInventoryLedgerDefaultRequest): Promise<InventoryLedgerDefaultDto> {
    return (await put<ApiResponse<InventoryLedgerDefaultDto>>(inventorySettingsEndpoints.setLedgerDefault(), data)).data.data;
  }
  async clearLedgerDefault(purposeCode: string): Promise<EmptyResponse> {
    return (await del<EmptyResponse>(inventorySettingsEndpoints.clearLedgerDefault(purposeCode))).data;
  }
  getOverrides(params?: LedgerOverrideQueryParams) { return getPaged<LedgerOverrideDto>(inventorySettingsEndpoints.overrides(), params); }
  async saveOverride(data: SaveLedgerOverrideRequest): Promise<LedgerOverrideDto> {
    return (await put<ApiResponse<LedgerOverrideDto>>(inventorySettingsEndpoints.overrides(), data)).data.data;
  }
  async deleteOverride(id: ID): Promise<EmptyResponse> {
    return (await del<EmptyResponse>(inventorySettingsEndpoints.deleteOverride(id))).data;
  }
}

class ItemService {
  getAll(params?: ItemQueryParams) { return getPaged<ItemDto>(itemEndpoints.list(), params); }
  getById(id: ID) { return getData<ItemDto>(itemEndpoints.getById(id)); }
  async getEnabledExtensions(): Promise<string[]> {
    return Array.from((await getData<string[]>(itemEndpoints.extensions())) || []);
  }
  async create(data: CreateItemRequest): Promise<ItemDto> {
    return (await post<ApiResponse<ItemDto>>(itemEndpoints.create(), data)).data.data;
  }
  async update(id: ID, data: UpdateItemRequest): Promise<ItemDto> {
    return (await put<ApiResponse<ItemDto>>(itemEndpoints.update(id), data)).data.data;
  }
  delete(id: ID, rowVersion: number) { return deleteVersioned(itemEndpoints.delete(id), rowVersion); }
  isCodeAvailable(code: string, excludeId?: ID) { return isAvailable(itemEndpoints.validateCode(code), excludeId); }
}

class StockReportService {
  /** Stock as at a date by item × location (or by item). */
  getStockPosition(params?: StockPositionQueryParams) { return getPaged<StockPositionRowDto>(stockReportEndpoints.stockPosition(), params); }
  /** One item's movements with running quantity and value. */
  getStockCard(params: StockCardQueryParams) { return getData<StockCardDto>(withQuery(stockReportEndpoints.stockCard(), params)); }
}

class OpeningStockService {
  getAll(params?: OpeningStockQueryParams) { return getPaged<OpeningStockDto>(openingStockEndpoints.list(), params); }
  getById(id: ID) { return getData<OpeningStockDto>(openingStockEndpoints.getById(id)); }
  async create(data: SaveOpeningStockRequest): Promise<OpeningStockDto> {
    return (await post<ApiResponse<OpeningStockDto>>(openingStockEndpoints.create(), data)).data.data;
  }
  async update(id: ID, data: SaveOpeningStockRequest): Promise<OpeningStockDto> {
    return (await put<ApiResponse<OpeningStockDto>>(openingStockEndpoints.update(id), data)).data.data;
  }
  /** Cancels the document: removes its stock and valuation; the document stays, marked cancelled. */
  cancel(id: ID, rowVersion: number) { return deleteVersioned(openingStockEndpoints.cancel(id), rowVersion); }
}

export const itemCategoryService = new ItemCategoryService();
export const classificationService = new ClassificationService();
export const stockLocationService = new StockLocationService();
export const inventorySettingsService = new InventorySettingsService();
export const itemService = new ItemService();
export const stockReportService = new StockReportService();
export const openingStockService = new OpeningStockService();
